import { Component, EventEmitter, OnDestroy, OnInit, Output, ChangeDetectorRef, ChangeDetectionStrategy, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';
import { TransCatgSyncService } from './trans-catg-sync.service';
import * as _ from 'lodash-es';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { JsonObjectFactoryService } from '../../../shared/_services/json-object-factory.service';
import { ConfirmationModalConfig } from '../../../shared/components/confirmation-modal/confirmation-modal.component';

@Component({
    selector: 'gtw-trans-catg-sync',
    templateUrl: './trans-catg-sync.component.html',
    styleUrls: ['./trans-catg-sync.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class TransCatgSyncComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);


  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private readonly subs = new Subscription();

  // Flags to prevent duplicate initialization
  private isInitialized = false;
  private isLoading = false;

  filterParams: any = {};
  baseURLs: any = { 'admin': '', 'api': '', 'custom': '', 'efile': '', 'pdfEngine': '', 'bulkPdfEngine': '' };
  rowData: any = {};

  // Component properties
  globalService: any = {};
  USER_SETTINGS: any = {};
  SERVER_CONFIG: any = {};
  GENERAL_CONFIG: any = {};

  // Form data properties
  filteredTaxYearData: any[] = [];
  taxYearData: any[] = [];
  selectedTaxYear: string = '';
  TaxYearKey: string = '';
  curentyear: string = '';
  scenario: string = '';
  jcd_key: string = '';

  scenarioDropDown: any[] = [];
  selectedScenario: string = '';
  showscenario: boolean = false;

  transCatgDetails: any[] = [];
  selectedTransCatg: string = '';
  showTransCatg: boolean = false;

  is_locked: any = null;

  // Loading state
  loading: boolean = false;
  loadingMessage: string = 'Loading...';

  // Confirmation modal
  showConfirmModal: boolean = false;
  confirmModalConfig: ConfirmationModalConfig = {
    title: 'Nightly Sync process for Trans-Catg',
    message: 'Are you sure to initiate the process for Trans-Catg?',
    iconClass: 'fa fa-sign-out',
    iconColor: 'txt-color-orange-dark',
    confirmButtonText: 'Yes',
    cancelButtonText: 'No',
    confirmButtonClass: 'btn-danger',
    cancelButtonClass: 'btn-default',
    showCancelButton: true,
    showConfirmButton: true,
    animationClass: 'animated fadeIn fast',
    loading: false,
    loadingText: 'Processing...'
  };

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private gtwSnackbarService: GtwSnackbarService,
    private transCatgSyncService: TransCatgSyncService,
    private jsonObjectFactory: JsonObjectFactoryService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    this.tryInit();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    this.isInitialized = false;
    this.isLoading = false;
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.globalService = header.globalParams || {};
    this.USER_SETTINGS = header.userSettingsData || {};
    this.SERVER_CONFIG = header.serverConfigData || {};
    this.filterParams = normalizeFilterParams(activeScreen);
    this.rowData = rowPayloadFromActiveScreen(activeScreen);
  }

  // Gate function to prevent duplicate initialization
  private tryInit(): void {
    if (this.isInitialized || this.isLoading) {
      return;
    }

    // Check if ALL required data is present before initializing
    if (!this.globalService || !this.GENERAL_CONFIG || Object.keys(this.GENERAL_CONFIG).length === 0) {
      return;
    }

    this.isInitialized = true;
    this.init();
  }

  init(): void {
    try {
      // Initialize component data from globalService
      // Fall back to the screen filter context: globalParams.tax_year can be empty here,
      // which would make the Tax Year dropdown filter reject every year (No results found).
      this.curentyear = this.globalService.tax_year || this.filterParams?.tax_year || '';
      this.scenario = this.globalService.scenario || this.filterParams?.scenario || '';
      this.jcd_key = this.globalService.jcd_key || this.filterParams?.jcd_key || '';

      // Check lock status
      this.checkLockStatus();

      // Load tax year dropdown
      this.getTaxYearDropDown();
    } catch (e) {
      console.error('Error in trans-catg-sync init:', e);
    }
  }

  checkLockStatus(): void {
    const lockparams = {
      "action_code": 'n10cpv',
      "scenario": this.scenario
    };

    // Create proper globalService structure expected by JsonObjectFactoryService
    const globalServiceWrapper = {
      globalParams: this.globalService
    };

    this.jsonObjectFactory.getJSONObj(
      this.GENERAL_CONFIG.base_url + '/loadJsonObject',
      lockparams,
      globalServiceWrapper,
      null,
      null
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        this.is_locked = data.jsonObject;
        if (this.is_locked != undefined && this.is_locked != null &&
          this.is_locked[0]?.LOCK_STATUS == 'Y') {
          this.gtwSnackbarService.openSnackBar(
            `Tax Return has already been filed for tax year ${this.filterParams.tax_year || this.curentyear}.`,
            'gtw-snackbar--red'
          );
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('Error checking lock status:', error);
        this.cdr.detectChanges();
      }
    );
  }

  private setLoading(isLoading: boolean, message: string = 'Loading...'): void {
    this.loading = isLoading;
    this.loadingMessage = message;
    this.cdr.detectChanges();
  }

  getTaxYearDropDown(): void {
    this.setLoading(true, 'Loading Tax Years...');

    const params: any = {
      "action_code": 'e83e1x',
      "tax_year": this.filterParams?.tax_year || this.globalService?.tax_year || '',
      "scenario": this.filterParams?.scenario || this.globalService?.scenario || '',
      "jcd_key": this.filterParams?.jcd_key || this.globalService?.jcd_key || '',
      "screen_key": this.filterParams?.screen_key || this.globalService?.screen_key || '',
      "jcd_ta_key": this.filterParams?.jcd_ta_key || this.globalService?.jcd_ta_key || '',
      "activity_key": this.filterParams?.activity_key || this.globalService?.activity_key || '',
      "screen_type": this.filterParams?.screen_type || this.globalService?.screen_type || 'custom',
      "is_locked": this.filterParams?.is_locked || this.globalService?.is_locked || 'N',
      "issue_id": this.filterParams?.issue_id || this.globalService?.issue_id || '',
      "issue_short_desc": this.filterParams?.issue_short_desc || this.globalService?.issue_short_desc || '',
      "issue_id_for_tx_save": this.filterParams?.issue_id_for_tx_save || this.globalService?.issue_id_for_tx_save || '',
      "scenarioCode": this.filterParams?.scenarioCode || this.filterParams?.scenario_code || this.globalService?.scenarioCode || 'RAF',
      "is_issue_key_enabled": this.filterParams?.is_issue_key_enabled || this.globalService?.is_issue_key_enabled || 'N'
    };

    // Create proper globalService structure expected by JsonObjectFactoryService
    const globalServiceWrapper = {
      globalParams: this.globalService
    };

    this.jsonObjectFactory.getJSONObj(
      this.GENERAL_CONFIG.base_url + '/loadJsonObject',
      params,
      globalServiceWrapper,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        this.taxYearData = _.filter(data.jsonObject, (p: any) => {
          return _.includes(['TAX_YEAR'], p.CODE_GROUP);
        });

        this.filteredTaxYearData = [];
        let currentYear = parseInt(this.curentyear, 10);
        if (isNaN(currentYear)) {
          // Last-resort fallback so the dropdown is never silently empty: anchor the
          // 5-year window on the newest tax year returned by the service.
          currentYear = this.taxYearData.reduce(
            (max: number, ty: any) => Math.max(max, parseInt(ty.CODE_DISP_DESC, 10) || 0),
            0
          );
        }
        for (let indx in this.taxYearData) {
          const desc = parseInt(this.taxYearData[indx].CODE_DISP_DESC, 10);
          if (this.taxYearData[indx].CODE_GROUP == 'TAX_YEAR' &&
            (desc <= currentYear && desc >= (currentYear - 5))) {
            this.filteredTaxYearData.push(this.taxYearData[indx]);
          }
        }
        // Sort in descending order (newest first) like AngularJS orderBy:'-CODE_DISP_DESC'
        this.filteredTaxYearData.sort((a, b) => {
          return parseInt(b.CODE_DISP_DESC) - parseInt(a.CODE_DISP_DESC);
        });
        this.cdr.markForCheck();
        this.setLoading(false);
      },
      (error: any) => {
        console.error('Error loading tax year dropdown:', error);
        this.setLoading(false);
      }
    );
  }

  getScenarioDropDown(): void {
    // Reset dependent dropdowns
    this.scenarioDropDown = [];
    this.selectedScenario = '';
    this.transCatgDetails = [];
    this.selectedTransCatg = '';
    this.showTransCatg = false;

    if (this.selectedTaxYear == undefined || this.selectedTaxYear == null || this.selectedTaxYear == '') {
      this.gtwSnackbarService.openSnackBar("Please select the Tax Year to get the Scenario.", 'gtw-snackbar--red');
      return;
    }

    this.setLoading(true, 'Loading Scenarios...');

    const params = {
      "action_code": '7q0wiy',
      "tax_year": this.selectedTaxYear
    };

    // Create proper globalService structure expected by JsonObjectFactoryService
    const globalServiceWrapper = {
      globalParams: this.globalService
    };

    this.jsonObjectFactory.getJSONObj(
      this.GENERAL_CONFIG.base_url + '/loadJsonObject',
      params,
      globalServiceWrapper,
      null,
      null
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        this.scenarioDropDown = data.jsonObject || [];
        // Sort by VALUE like AngularJS orderBy:'VALUE'
        if (this.scenarioDropDown.length > 0) {
          this.scenarioDropDown.sort((a, b) => {
            return a.VALUE - b.VALUE;
          });
        }
        this.showscenario = true;
        this.cdr.markForCheck();
        this.setLoading(false);
      },
      (error: any) => {
        console.error('Error loading scenario dropdown:', error);
        this.setLoading(false);
      }
    );
  }

  transCatgInfo(): void {
    // Reset trans category dropdown
    this.transCatgDetails = [];
    this.selectedTransCatg = '';

    this.setLoading(true, 'Loading Trans Categories...');

    const params = {
      "action_code": "lrjifk",
      "tax_year": this.selectedTaxYear
    };

    // Create proper globalService structure expected by JsonObjectFactoryService
    const globalServiceWrapper = {
      globalParams: this.globalService
    };

    this.jsonObjectFactory.getJSONObj(
      this.GENERAL_CONFIG.base_url + '/loadJsonObject',
      params,
      globalServiceWrapper,
      null,
      null
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        this.transCatgDetails = Object.assign([], data.jsonObject || []);
        // Sort by TRANS_CATG_KEY like AngularJS orderBy:'TRANS_CATG_KEY'
        if (this.transCatgDetails.length > 0) {
          this.transCatgDetails.sort((a, b) => {
            return a.TRANS_CATG_KEY - b.TRANS_CATG_KEY;
          });
        }
        this.showTransCatg = true;
        this.cdr.markForCheck();
        this.setLoading(false);
      },
      (error: any) => {
        console.error('Error loading trans category details:', error);
        this.setLoading(false);
      }
    );
  }

  // Show confirmation dialog before sync
  onTransCatgSyncClick(): void {
    // Validation
    if (this.selectedTaxYear == undefined || this.selectedTaxYear == null || this.selectedTaxYear == '') {
      this.gtwSnackbarService.openSnackBar("Please select a valid Tax Year.", 'gtw-snackbar--red');
      return;
    }
    if (this.selectedScenario == undefined || this.selectedScenario == null || this.selectedScenario == '') {
      this.gtwSnackbarService.openSnackBar("Please select a valid Scenario.", 'gtw-snackbar--red');
      return;
    }
    if (this.selectedTransCatg == undefined || this.selectedTransCatg == null || this.selectedTransCatg == '') {
      this.gtwSnackbarService.openSnackBar("Please select a valid Trans Category.", 'gtw-snackbar--red');
      return;
    }

    // Show confirmation modal
    this.showConfirmModal = true;
    this.cdr.detectChanges();
  }

  onConfirmSync(): void {
    this.showConfirmModal = false;
    this.cdr.detectChanges();
    this.transCatgSync();
  }

  onCancelConfirm(): void {
    this.showConfirmModal = false;
    this.cdr.detectChanges();
  }

  // Execute sync process
  transCatgSync(): void {
    this.setLoading(true, 'Checking lock status...');

    // First check lock status
    const lockparams = {
      "action_code": 'n10cpv',
      "tax_year": this.selectedTaxYear,
      "p_consol_group_key": '1',
      "jcd_key": this.jcd_key
    };

    // Create proper globalService structure expected by JsonObjectFactoryService
    const globalServiceWrapper = {
      globalParams: this.globalService
    };

    this.jsonObjectFactory.getJSONObj(
      this.GENERAL_CONFIG.base_url + '/loadJsonObject',
      lockparams,
      globalServiceWrapper,
      null,
      null
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        this.is_locked = data.jsonObject;
        console.log("Lock check response - is_locked:", this.is_locked);

        // Check if locked:
        // - If jsonObject is empty array [] → NOT locked (no lock record exists) → proceed
        // - If jsonObject has record with LOCK_STATUS == 'N' → NOT locked → proceed
        // - If jsonObject has record with LOCK_STATUS == 'Y' → LOCKED → show error
        const isNotLocked = !this.is_locked || 
                           this.is_locked.length === 0 || 
                           this.is_locked[0]?.LOCK_STATUS == 'N';

        if (isNotLocked) {
          // Proceed with sync (loading continues)
          this.loadingMessage = 'Initiating sync process...';
          this.cdr.detectChanges();
          this.executeSync();
        } else {
          this.setLoading(false);
          this.gtwSnackbarService.openSnackBar(
            "Cannot proceed with Sync Process. Tax Return has already been filed for selected Tax Year.",
            'gtw-snackbar--red'
          );
        }
      },
      (error: any) => {
        console.error('Error checking lock status:', error);
        this.setLoading(false);
        this.gtwSnackbarService.openSnackBar(
          "Error checking lock status. Please try again.",
          'gtw-snackbar--red'
        );
      }
    );
  }

  private executeSync(): void {
    const params = {
      "sso_id": this.USER_SETTINGS?.user?.sso_id || '',
      "action_code": 'ghtqbf',
      "tax_year": this.selectedTaxYear,
      "scenario": this.selectedScenario,
      "jcd_key": this.jcd_key,
      "trans_catg_key": this.selectedTransCatg
    };

    this.transCatgSyncService.saveTransCatgSync(
      this.GENERAL_CONFIG.base_url,
      params,
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.setLoading(false);
        if (response.callSuccess != 1) {
          this.gtwSnackbarService.openSnackBar(response.errorMessage, 'gtw-snackbar--red');
          return;
        }
        this.gtwSnackbarService.openSnackBar(
          "Trans Catg Sync initiated. Email will be sent, after the process is complete.",
          'gtw-snackbar--green'
        );
        // Emit refresh event for AngularJS integration
        this.requestGridUpdate.emit('refresh');
      },
      (error: any) => {
        console.error('Error initiating trans catg sync:', error);
        this.setLoading(false);
        this.gtwSnackbarService.openSnackBar(
          "Error occurred while initiating sync process. Please try again.",
          'gtw-snackbar--red'
        );
      }
    );
  }

  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }
}
